import apiClient from "@toothfairy/shared-api/apiClient";
import secureRequest from "@toothfairy/shared-api/secureAPI";
import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import envConfig from "../../../envConfig";
import { ENABLE_CONSUMPTION_BILLING, USD_PER_UOI } from "../../Constants/billing";

// ---------------------------------------------------------------------------
// Credits / consumption billing contract (real backend).
//
// Credit balance lives on the Stripe customer object (negative balance = credit
// the customer holds). Auto-recharge config lives in the Stripe customer
// `metadata` field as `rechargeConfig` (JSON). Recharge is a Stripe Checkout
// session (mode: "payment") created server-side; the frontend redirects to the
// returned `url`.
//
// Endpoints (REST, authed via the Cognito session):
//   POST   /billing/recharge    body { amountUsd }       -> { url, sessionId }
//   GET    /billing/customer                            -> { balance, metadata, ... }
//   POST   /billing/customer    body { rechargeConfig }  -> updated
//
// The per-workspace UoI budget (maxUsers * UOI_PER_SEAT) is NOT managed here —
// it stays on the workspace. This module owns the ACCOUNT-level monthly spend
// cap (spendCap metadata) and the auto-recharge config (rechargeConfig metadata).
// ---------------------------------------------------------------------------

const BILLING_BASE = `${envConfig.REACT_APP_REST_ENDPOINT}/billing`;

const authedPayload = async () => AppAuth.getCurrentSession();

// Public: create a Stripe Checkout recharge session for `amountUsd`.
// Returns { url, sessionId } — the caller redirects to `url` (web) or opens it
// (native). Credit is granted server-side on checkout.session.completed.
const rechargeCredits = async ({ amountUsd, returnTo } = {}) => {
  if (!ENABLE_CONSUMPTION_BILLING) return null;
  if (!amountUsd || amountUsd <= 0) {
    throw new Error("A recharge amount is required.");
  }
  const payload = await authedPayload();
  const body = { amountUsd };
  if (returnTo) body.returnTo = returnTo;
  return secureRequest(
    apiClient.apiClient,
    "POST",
    `${BILLING_BASE}/recharge`,
    null,
    body,
    true,
    null,
    payload,
  );
};

// Public: synchronously confirm a recharge after the user returns from Stripe
// Checkout. Grants the credit (idempotently) if the webhook hasn't yet, and
// returns the updated balance. Closes the race where the redirect beats the
// async checkout.session.completed webhook.
const confirmRechargeSession = async ({ sessionId } = {}) => {
  if (!ENABLE_CONSUMPTION_BILLING) return null;
  if (!sessionId) return null;
  const payload = await authedPayload();
  return secureRequest(
    apiClient.apiClient,
    "POST",
    `${BILLING_BASE}/recharge/confirm`,
    null,
    { sessionId },
    true,
    null,
    payload,
  );
};

// Public: read the live credit balance from the Stripe customer object.
// Returns { balanceUsd, balanceUoI, customer }.
const getCreditBalance = async () => {
  if (!ENABLE_CONSUMPTION_BILLING) return null;
  const payload = await authedPayload();
  const response = await secureRequest(
    apiClient.apiClient,
    "GET",
    `${BILLING_BASE}/customer`,
    null,
    null,
    true,
    null,
    payload,
  );
  // secureRequest returns the apisauce wrapper ({ ok, data, ... }); the Stripe
  // customer object is in `.data`. Reading balance off the wrapper yields
  // undefined -> 0, which is why the UI flashed to $0 after refresh.
  const customer = response?.data ?? response;
  // Stripe stores credit as a negative integer (cents).
  const balanceUsd = customer?.balance ? customer.balance / -100 : 0;
  return { balanceUsd, balanceUoI: Math.round(balanceUsd / USD_PER_UOI), customer };
};

// Default auto-recharge config when the customer has none stored yet.
const DEFAULT_RECHARGE_CONFIG = {
  autoRecharge: { enabled: false, thresholdUsd: 5, amountUsd: 25 },
};

// Default account-level spend cap when the customer has none stored yet.
// null = no cap until the user sets one.
const DEFAULT_SPEND_CAP = { monthlyMaxUsd: null };

// Parse the recharge config out of the Stripe customer `metadata` field. The
// backend stores it as a JSON string under the `rechargeConfig` metadata key.
const parseRechargeConfig = (customer) => {
  const raw = customer?.metadata?.rechargeConfig;
  if (!raw) return { ...DEFAULT_RECHARGE_CONFIG };
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return {
      autoRecharge:
        parsed?.autoRecharge ?? DEFAULT_RECHARGE_CONFIG.autoRecharge,
    };
  } catch (e) {
    return { ...DEFAULT_RECHARGE_CONFIG };
  }
};

// Parse the account-level monthly spend cap out of the Stripe customer
// `metadata` field. Stored as a JSON string under the `spendCap` key:
// { monthlyMaxUsd: number | null }.
const parseSpendCap = (customer) => {
  const raw = customer?.metadata?.spendCap;
  if (!raw) return { ...DEFAULT_SPEND_CAP };
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const monthlyMaxUsd = Number(parsed?.monthlyMaxUsd);
    return {
      monthlyMaxUsd: Number.isFinite(monthlyMaxUsd) && monthlyMaxUsd > 0 ? monthlyMaxUsd : null,
    };
  } catch (e) {
    return { ...DEFAULT_SPEND_CAP };
  }
};

// Public: read the user's recharge config (auto-recharge) AND account-level
// spend cap from Stripe metadata. Returns { autoRecharge, spendCap }.
const getRechargeConfig = async () => {
  if (!ENABLE_CONSUMPTION_BILLING) return null;
  const payload = await authedPayload();
  const response = await secureRequest(
    apiClient.apiClient,
    "GET",
    `${BILLING_BASE}/customer`,
    null,
    null,
    true,
    null,
    payload,
  );
  // secureRequest returns the apisauce wrapper; the Stripe customer is in `.data`.
  const customer = response?.data ?? response;
  return {
    ...parseRechargeConfig(customer),
    spendCap: parseSpendCap(customer),
  };
};

// Public: persist the recharge config (auto-recharge) to Stripe customer
// metadata. `config` is { autoRecharge: { enabled, thresholdUsd, amountUsd } }.
const updateRechargeConfig = async (config = {}) => {
  if (!ENABLE_CONSUMPTION_BILLING) return null;
  const payload = await authedPayload();
  return secureRequest(
    apiClient.apiClient,
    "POST",
    `${BILLING_BASE}/customer`,
    null,
    { rechargeConfig: config },
    true,
    null,
    payload,
  );
};

// Public: persist the account-level monthly spend cap to Stripe customer
// metadata. `cap` is { monthlyMaxUsd: number | null }.
const updateSpendCap = async (cap = {}) => {
  if (!ENABLE_CONSUMPTION_BILLING) return null;
  const payload = await authedPayload();
  return secureRequest(
    apiClient.apiClient,
    "POST",
    `${BILLING_BASE}/customer`,
    null,
    { spendCap: cap },
    true,
    null,
    payload,
  );
};

// Public: persist BOTH rechargeConfig and spendCap in a SINGLE POST. The backend
// does a read-merge-write per request, so sending them as two concurrent calls
// races and they clobber each other's metadata. One combined call is atomic.
const updateBillingSettings = async ({ spendCap, rechargeConfig } = {}) => {
  if (!ENABLE_CONSUMPTION_BILLING) return null;
  const payload = await authedPayload();
  const body = {};
  if (spendCap !== undefined) body.spendCap = spendCap;
  if (rechargeConfig !== undefined) body.rechargeConfig = rechargeConfig;
  return secureRequest(
    apiClient.apiClient,
    "POST",
    `${BILLING_BASE}/customer`,
    null,
    body,
    true,
    null,
    payload,
  );
};

export const API_Credits = {
  getCreditBalance,
  rechargeCredits,
  confirmRechargeSession,
  getRechargeConfig,
  updateRechargeConfig,
  updateSpendCap,
  updateBillingSettings,
  parseRechargeConfig,
  parseSpendCap,
};

export default API_Credits;